import { gql } from "graphql-request";
import { Injectable, createInterfaceId } from "@gitlab/needle";
import { GitLabApiService, ifVersionGte } from "@gitlab-org/core";
import { CompositeDisposable } from "@gitlab-org/disposable";
import { Logger } from "@gitlab-org/logging";
//#region src/operations/ai/chat/available_models.ts
/**
* Available chat models for Duo Agent Platform including GitLab default and pinned model selection.
*/
const AiChatAvailableModelsQuery = {
	supportedSinceInstanceVersion: "18.5.0",
	query: gql`
    query lsp_aiChatAvailableModels($rootNamespaceId: GroupID!) {
      metadata {
        featureFlags(names: ["ai_user_model_switching"]) {
          enabled
          name
        }
        version
      }

      aiChatAvailableModels(rootNamespaceId: $rootNamespaceId) @gl_introduced(version: "18.4.0") {
        defaultModel {
          ...modelFields
        }
        selectableModels {
          ...modelFields
        }
        pinnedModel @gl_introduced(version: "18.5.0") {
          ...modelFields
        }
      }
    }

    fragment modelFields on AiModelSelectionOfferedModel {
      name
      ref
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error querying available models: ${event.err}`, { cause: event.err });
		return {
			aiChatAvailableModels: null,
			metadata: null
		};
	}
};
//#endregion
//#region src/operations/ai/chat/foundational_chat_agents.ts
/**
* Available chat agents for Duo Agent Platform including GitLab default and pinned model selection.
*/
const AiChatFoundationalAgentsQuery = {
	supportedSinceInstanceVersion: "18.6.0",
	query: gql`
    query lsp_aiFoundationalAgents($projectId: ProjectID!, $namespaceId: NamespaceID!) {
      aiFoundationalChatAgents(projectId: $projectId, namespaceId: $namespaceId) {
        nodes {
          id
          name
          description
          referenceWithVersion
          selectableInChat @gl_introduced(version: "18.11.0")
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error querying foundational agents: ${event.err}`, { cause: event.err });
		return { aiFoundationalChatAgents: { nodes: [] } };
	}
};
//#endregion
//#region src/operations/ai/flows_metadata.ts
/**
* A single capability the instance advertises for Duo Agent Platform flows.
* `metadata` is arbitrary JSON whose shape varies per capability (for the
* `duo_developer` capability it carries the registry coordinates of the
* default flow: `flow_config_id`, `flow_config_schema_version`, `flow_version`).
*/
/**
* Capabilities the instance advertises for Duo Agent Platform flows. The
* client reads the `duo_developer` capability to decide the default flow a new
* Duo CLI session should run; its presence means "run the developer flow", its
* absence means "fall back to legacy chat". The full capability list also
* mirrors (and is intended to eventually replace) the `server_capabilities`
* computed by the REST `direct_access` endpoint, so callers can source
* capabilities from here without needing a workflow token — see
* `ee/app/services/ai/flows_metadata_service.rb` on the Rails side.
*
* `namespaceId`/`projectId` scope namespace/project-dependent capabilities
* (e.g. `advanced_search`, `tool_call_approval`); omitting them only returns
* namespace-independent capabilities.
*/
const AiFlowsMetadataQuery = {
	supportedSinceInstanceVersion: "19.2.0",
	query: gql`
    query lsp_aiFlowsMetadata($namespaceId: NamespaceID, $projectId: ProjectID) {
      aiFlowsMetadata(namespaceId: $namespaceId, projectId: $projectId) {
        capabilities {
          name
          metadata
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error querying AI flows metadata: ${event.err}`, { cause: event.err });
		return { aiFlowsMetadata: null };
	}
};
//#endregion
//#region src/operations/ai/workflows.ts
/**
* Duo workflows for Duo Agent Platform
*/
const DuoWorkflowWorkflowsQuery = {
	supportedSinceInstanceVersion: "18.2.0",
	query: gql`
    query getUserWorkflows(
      $type: String
      $environment: WorkflowEnvironment
      $search: String
      $after: String
      $before: String
      $first: Int
      $last: Int
    ) {
      duoWorkflowWorkflows(
        type: $type
        environment: $environment
        search: $search
        first: $first
        after: $after
        last: $last
        before: $before
      ) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            id
            projectId
            humanStatus
            project {
              fullPath
            }
            updatedAt
            goal
            archived
            workflowDefinition
            agentName @gl_introduced(version: "18.8.0")
            aiCatalogItemVersionId @gl_introduced(version: "18.4.0")
            latestCheckpoint {
              duoMessages {
                content
                messageType
                toolInfo
                status
              }
            }
          }
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error querying workflows: ${event.err}`, { cause: event.err });
		return { duoWorkflowWorkflows: {
			edges: null,
			pageInfo: {
				startCursor: "",
				endCursor: "",
				hasNextPage: false,
				hasPreviousPage: false
			}
		} };
	}
};
//#endregion
//#region src/operations/ai/delete_workflow.ts
const DeleteDuoWorkflowsWorkflowMutation = {
	supportedSinceInstanceVersion: "18.1.0",
	query: gql`
      mutation deleteDuoWorkflowsWorkflow($input: DeleteDuoWorkflowsWorkflowInput!) {
        deleteDuoWorkflowsWorkflow(input: $input) {
          clientMutationId
          errors
          success
        }
      }
    `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error deleting a workflow: ${event.err}`, { cause: event.err });
		return { deleteDuoWorkflowsWorkflow: {
			clientMutationId: null,
			errors: [],
			success: false
		} };
	}
};
//#endregion
//#region src/operations/ai/update_tool_call_approvals.ts
const fallback = (event) => {
	if (event.err) throw new Error(`Error updating tool call approvals: ${event.err}`, { cause: event.err });
	if (event.unsupported) return { updateDuoWorkflowToolCallApprovals: {
		workflow: null,
		errors: []
	} };
	return { updateDuoWorkflowToolCallApprovals: {
		workflow: null,
		errors: ["Failed to update tool call approvals"]
	} };
};
/**
* Legacy mutation document, without the `approvalSource` argument. Valid against every
* GitLab instance that supports the mutation at all (18.9.0+). Use this variant unless
* the instance advertises the `tool_call_approval_source` server capability; see
* `UpdateToolCallApprovalsWithSourceMutation` below.
*
* Note: `supportedSinceInstanceVersion` is only enforced when the operation is executed
* through `DefaultGraphQLService.execute` (or a request-level `supportedSinceInstanceVersion`
* is passed to `fetchFromApi`). The tool-approval persistence path in
* `packages/lib_tool_approval/src/persistence.ts` sends the bare query string, so document
* selection there is gated on server capabilities instead of instance versions.
*/
const UpdateToolCallApprovalsMutation = {
	supportedSinceInstanceVersion: "18.9.0",
	query: gql`
    mutation UpdateToolCallApprovals(
      $workflowId: AiDuoWorkflowsWorkflowID!
      $toolName: String!
      $toolCallArgs: JSON
      $pattern: String
    ) {
      updateDuoWorkflowToolCallApprovals(
        input: {
          workflowId: $workflowId
          toolName: $toolName
          toolCallArgs: $toolCallArgs
          pattern: $pattern
        }
      ) {
        workflow {
          id
          toolCallApprovals
        }
        errors
      }
    }
  `,
	fallback
};
/**
* Variant of the mutation that also records the approval's source (audit provenance).
*
* The `DuoWorkflowToolCallApprovalSource` enum and the `approvalSource` argument ship in
* GitLab 19.3.0 (gitlab-org/gitlab!247456, gitlab-org/gitlab#603370). GraphQL validates the
* whole document against the schema, so on older instances this document fails validation
* with "Unknown type" even when the `$approvalSource` variable is omitted. Callers must
* only select this document when the instance advertises the `tool_call_approval_source`
* server capability (advertised by 19.3.0+ Rails); otherwise use
* `UpdateToolCallApprovalsMutation`. See `supportsApprovalSource` in
* `packages/lib_tool_approval/src/capability_checker.ts`.
*/
const UpdateToolCallApprovalsWithSourceMutation = {
	supportedSinceInstanceVersion: "19.3.0",
	query: gql`
      mutation UpdateToolCallApprovalsWithSource(
        $workflowId: AiDuoWorkflowsWorkflowID!
        $toolName: String!
        $toolCallArgs: JSON
        $pattern: String
        $approvalSource: DuoWorkflowToolCallApprovalSource
      ) {
        updateDuoWorkflowToolCallApprovals(
          input: {
            workflowId: $workflowId
            toolName: $toolName
            toolCallArgs: $toolCallArgs
            pattern: $pattern
            approvalSource: $approvalSource
          }
        ) {
          workflow {
            id
            toolCallApprovals
          }
          errors
        }
      }
    `,
	fallback
};
//#endregion
//#region src/operations/ai/update_agent_privileges.ts
const UpdateAgentPrivilegesMutation = {
	supportedSinceInstanceVersion: "19.2.0",
	query: gql`
    mutation UpdateAgentPrivileges(
      $workflowId: AiDuoWorkflowsWorkflowID!
      $agentPrivileges: [Int!]
      $preApprovedAgentPrivileges: [Int!]
    ) {
      updateDuoWorkflowAgentPrivileges(
        input: {
          workflowId: $workflowId
          agentPrivileges: $agentPrivileges
          preApprovedAgentPrivileges: $preApprovedAgentPrivileges
        }
      ) {
        workflow {
          id
        }
        errors
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error updating agent privileges: ${event.err}`, { cause: event.err });
		if (event.unsupported) return { updateDuoWorkflowAgentPrivileges: {
			workflow: null,
			errors: []
		} };
		return { updateDuoWorkflowAgentPrivileges: {
			workflow: null,
			errors: ["Failed to update agent privileges"]
		} };
	}
};
//#endregion
//#region src/operations/ai/catalog/list_flows.ts
const ListCatalogFlowsQuery = {
	supportedSinceInstanceVersion: "18.8.0",
	query: gql`
    query lsp_listCatalogFlows($search: String, $first: Int, $after: String) {
      aiCatalogItems(itemTypes: [FLOW], search: $search, first: $first, after: $after) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            id
            name
            description
            public
            updatedAt
            project {
              id
              fullPath
            }
            latestVersion {
              ... on AiCatalogFlowVersion {
                id
                versionName
                humanVersionName
                released
                releasedAt
                updatedAt
              }
            }
          }
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error listing catalog flows: ${event.err}`, { cause: event.err });
		return { aiCatalogItems: {
			edges: null,
			pageInfo: {
				startCursor: "",
				endCursor: "",
				hasNextPage: false,
				hasPreviousPage: false
			}
		} };
	}
};
//#endregion
//#region src/operations/ai/catalog/get_flow.ts
const GetCatalogFlowQuery = {
	supportedSinceInstanceVersion: "18.8.0",
	query: gql`
    query lsp_getCatalogFlow($id: AiCatalogItemID!) {
      aiCatalogItem(id: $id) {
        id
        name
        description
        public
        updatedAt
        project {
          id
          fullPath
        }
        latestVersion {
          ... on AiCatalogFlowVersion {
            id
            versionName
            humanVersionName
            released
            releasedAt
            updatedAt
            definition
          }
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error fetching catalog flow: ${event.err}`, { cause: event.err });
		return { aiCatalogItem: null };
	}
};
//#endregion
//#region src/operations/ai/catalog/create_flow.ts
const CreateCatalogFlowMutation = {
	supportedSinceInstanceVersion: "18.8.0",
	query: gql`
    mutation lsp_createCatalogFlow(
      $projectId: ProjectID!
      $name: String!
      $description: String!
      $public: Boolean!
      $definition: String!
    ) {
      aiCatalogFlowCreate(
        input: {
          projectId: $projectId
          name: $name
          description: $description
          public: $public
          definition: $definition
        }
      ) {
        item {
          id
          name
          description
          public
          updatedAt
          project {
            id
            fullPath
          }
          latestVersion {
            ... on AiCatalogFlowVersion {
              id
              versionName
              humanVersionName
              released
              releasedAt
              updatedAt
            }
          }
        }
        errors
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error creating catalog flow: ${event.err}`, { cause: event.err });
		throw new Error("Catalog flow creation requires GitLab 18.8 or later");
	}
};
//#endregion
//#region src/operations/ai/catalog/update_flow.ts
const UpdateCatalogFlowMutation = {
	supportedSinceInstanceVersion: "18.8.0",
	query: gql`
    mutation lsp_updateCatalogFlow($id: AiCatalogItemID!, $definition: String!) {
      aiCatalogFlowUpdate(input: { id: $id, definition: $definition }) {
        item {
          id
          name
          description
          public
          updatedAt
          project {
            id
            fullPath
          }
          latestVersion {
            ... on AiCatalogFlowVersion {
              id
              versionName
              humanVersionName
              released
              releasedAt
              updatedAt
            }
          }
        }
        errors
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error updating catalog flow: ${event.err}`, { cause: event.err });
		throw new Error("Catalog flow update requires GitLab 18.8 or later");
	}
};
//#endregion
//#region src/operations/project/get_issue_related_merge_requests.ts
/**
* Checks whether an issue has any related (linked) merge requests — not just
* ones that close it (`Issue.mergeRequestsCount` only covers the latter).
*
* `Issue.relatedMergeRequests` is capped server-side to one issue per request
* (`FieldCallCount limit: 1`), so this deliberately fetches a single issue;
* callers fan out one request per candidate issue. Used to exclude
* already-in-progress issues from the welcome card's "start" prompts.
*/
const GetIssueRelatedMergeRequestsQuery = {
	supportedSinceInstanceVersion: "15.9.0",
	query: gql`
      query lsp_getIssueRelatedMergeRequests($projectPath: ID!, $iid: String!) {
        project(fullPath: $projectPath) {
          id
          issue(iid: $iid) {
            iid
            relatedMergeRequests(first: 1) {
              nodes {
                iid
              }
            }
          }
        }
      }
    `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error fetching related merge requests for issue: ${event.err}`, { cause: event.err });
		return { project: null };
	}
};
//#endregion
//#region src/operations/project/get_user_contextual_work_items.ts
/** Values of the GraphQL `PipelineStatusEnum`. */
/**
* Fetches the current user's open merge requests and assigned issues in a
* single project, with just enough signal to derive contextual prompts for
* the TUI welcome card (failing pipeline, merge conflicts, unresolved
* discussions, issue activity). See
* https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/work_items/2653.
*
* The same MR can appear in both `authoredMergeRequests` and
* `assignedMergeRequests`; callers should deduplicate by `iid`.
*/
const GetUserContextualWorkItemsQuery = {
	supportedSinceInstanceVersion: "0.0.0",
	query: gql`
    query lsp_getUserContextualWorkItems(
      $projectPath: ID!
      $username: String!
      $updatedAfter: Time!
    ) {
      project(fullPath: $projectPath) {
        id
        authoredMergeRequests: mergeRequests(
          state: opened
          authorUsername: $username
          sort: UPDATED_DESC
          first: 10
        ) {
          nodes {
            ...LspContextualMergeRequest
          }
        }
        assignedMergeRequests: mergeRequests(
          state: opened
          assigneeUsername: $username
          sort: UPDATED_DESC
          first: 10
        ) {
          nodes {
            ...LspContextualMergeRequest
          }
        }
        issues(
          state: opened
          assigneeUsernames: [$username]
          sort: UPDATED_DESC
          updatedAfter: $updatedAfter
          first: 10
        ) {
          nodes {
            iid
            title
            updatedAt
            userNotesCount
            mergeRequestsCount
          }
        }
      }
    }

    fragment LspContextualMergeRequest on MergeRequest {
      iid
      title
      updatedAt
      conflicts
      headPipeline {
        status
      }
      resolvableDiscussionsCount @gl_introduced(version: "17.7.0")
      resolvedDiscussionsCount @gl_introduced(version: "17.7.0")
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error fetching contextual work items: ${event.err}`, { cause: event.err });
		return { project: null };
	}
};
//#endregion
//#region src/operations/project/get_user_project_permissions.ts
/**
* Subset of `Project.userPermissions` we surface to the canvas.
*
* `pushCode`, `adminProject`, `removeProject` give us a coarse role tier
* (Developer / Maintainer / Owner) for the session indicator.
*
* `adminAiCatalogItem` and `readAiCatalogItem` are the ground truth for
* AI-Catalog-specific gating — namespace policies can grant these to
* roles below the typical "Maintainer+" default, so we ask the server
* rather than inferring from role.
*
* The AI Catalog fields were introduced in 18.3 as Experiment and are
* already marked deprecated upstream; if/when a non-deprecated successor
* lands, this is where to swap. They may be `null` on instances that
* stripped them via the `@gl_introduced` directive.
*/
const GetUserProjectPermissionsQuery = {
	supportedSinceInstanceVersion: "0.0.0",
	query: gql`
    query lsp_getUserProjectPermissions($projectPath: ID!) {
      project(fullPath: $projectPath) {
        id
        userPermissions {
          pushCode
          adminProject
          removeProject
          adminAiCatalogItem @gl_introduced(version: "18.3.0")
          readAiCatalogItem @gl_introduced(version: "18.3.0")
        }
      }
    }
  `,
	fallback: (event) => {
		if (event.err) throw new Error(`Error fetching project permissions: ${event.err}`, { cause: event.err });
		return { project: null };
	}
};
//#endregion
//#region src/operations/index.ts
const KnownGraphQLOperations = {
	aiChatAvailableModels: AiChatAvailableModelsQuery,
	aiFoundationalAgents: AiChatFoundationalAgentsQuery,
	aiFlowsMetadata: AiFlowsMetadataQuery,
	duoWorkflows: DuoWorkflowWorkflowsQuery,
	deleteWorkflow: DeleteDuoWorkflowsWorkflowMutation,
	updateToolCallApprovals: UpdateToolCallApprovalsMutation,
	updateAgentPrivileges: UpdateAgentPrivilegesMutation,
	getUserProjectPermissions: GetUserProjectPermissionsQuery,
	getUserContextualWorkItems: GetUserContextualWorkItemsQuery,
	getIssueRelatedMergeRequests: GetIssueRelatedMergeRequestsQuery
};
//#endregion
//#region src/service.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const GraphQLService = createInterfaceId("GraphQLService");
let _DefaultGraphQLServic;
(class {
	static {
		[_DefaultGraphQLServic, _initClass] = _applyDecs(this, [Injectable(GraphQLService, [GitLabApiService, Logger])], []).c;
	}
	#disposables = new CompositeDisposable();
	#api;
	#instanceVersion;
	#logger;
	constructor(api, logger) {
		this.#api = api;
		this.#instanceVersion = this.#api.instanceInfo?.instanceVersion;
		this.#logger = logger;
		this.#disposables.add(this.#api.onApiReconfigured((data) => {
			if (data.isInValidState) this.#instanceVersion = data.instanceInfo.instanceVersion;
		}));
	}
	async execute({ supportedSinceInstanceVersion, fallback, query }, variables = {}, signal) {
		if (!this.#laterVersion(supportedSinceInstanceVersion)) {
			this.#logger.debug(`GitLab ${supportedSinceInstanceVersion} is required to use this operation falling back to default behaviour.`);
			return fallback({ unsupported: "future_field" });
		}
		try {
			return await this.#api.fetchFromApi({
				type: "graphql",
				query,
				variables: variables ?? {},
				signal
			});
		} catch (err) {
			return fallback({ err });
		}
	}
	dispose() {
		this.#disposables.dispose();
	}
	#laterVersion(supportedSinceInstanceVersion) {
		if (supportedSinceInstanceVersion) return ifVersionGte(this.#instanceVersion, supportedSinceInstanceVersion, () => true, () => false);
		return false;
	}
	static {
		_initClass();
	}
});
//#endregion
export { AiChatAvailableModelsQuery, AiChatFoundationalAgentsQuery, AiFlowsMetadataQuery, CreateCatalogFlowMutation, _DefaultGraphQLServic as DefaultGraphQLService, DeleteDuoWorkflowsWorkflowMutation, DuoWorkflowWorkflowsQuery, GetCatalogFlowQuery, GetIssueRelatedMergeRequestsQuery, GetUserContextualWorkItemsQuery, GetUserProjectPermissionsQuery, GraphQLService, KnownGraphQLOperations, ListCatalogFlowsQuery, UpdateAgentPrivilegesMutation, UpdateCatalogFlowMutation, UpdateToolCallApprovalsMutation, UpdateToolCallApprovalsWithSourceMutation };

//# sourceMappingURL=index.mjs.map